import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Trash2, Save, Wifi, Package, Download, CheckCircle2, AlertCircle, ChevronsUpDown, ChevronUp, ChevronDown } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors";

const EMPTY = {
  name: "", speed_download_mbps: 100, speed_upload_mbps: 40,
  monthly_price: 0, activation_fee: 0, contract_months: 0,
  description: "", features: [], technologies: [],
  syncro_product_code: "", syncro_product_prefix: "",
  partner_code: "", active: true, sort_order: 0,
};

// Canonical NBN delivery technologies — order is deliberate (fibre
// tiers first, wireless last) so the UI checkbox row and the tech
// pills on the price list read the same way.
const NBN_TECHS = ["FTTP", "HFC", "FTTB", "FTTC", "FTTN", "FW"];

export default function AdminInternetPlans() {
  const [plans, setPlans] = useState(null);
  // Column-header sort state — clickable headers on the plan table.
  // ``sortBy`` maps to the underlying plan field (or a synthetic key
  // like "speed" that composes download+upload). Third click clears
  // the sort back to the default "sort_order asc" ordering that
  // matches what the wizard sees.
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  function toggleSort(field) {
    if (sortBy !== field) { setSortBy(field); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    // Third click clears — restores default ordering.
    setSortBy(""); setSortDir("asc");
  }

  const sortedPlans = useMemo(() => {
    if (plans === null) return null;
    if (!sortBy) return plans;
    const key = sortBy;
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (p) => {
      if (key === "speed") return (p.speed_download_mbps || 0) * 100000 + (p.speed_upload_mbps || 0);
      if (key === "monthly_price") return p.monthly_price || 0;
      if (key === "partner_code") return (p.partner_code || "").toLowerCase();
      if (key === "syncro") return (p.resolved_syncro_code || "").toLowerCase();
      return (p[key] ?? "").toString().toLowerCase();
    };
    return [...plans].sort((a, b) => {
      const av = val(a); const bv = val(b);
      if (av === bv) return 0;
      return av > bv ? dir : -dir;
    });
  }, [plans, sortBy, sortDir]);
  const [partners, setPartners] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);

  async function reload() {
    try {
      const [pl, pt] = await Promise.all([
        api.get("/commissions/internet-plans"),
        api.get("/commissions/partners"),
      ]);
      setPlans(pl.data.plans || []);
      // /commissions/partners returns a raw array, not {partners:[]}
      setPartners(Array.isArray(pt.data) ? pt.data : (pt.data.partners || []));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load plans");
    }
  }
  useEffect(() => { reload(); }, []);

  async function del(p) {
    if (!window.confirm(`Delete ${p.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/commissions/internet-plans/${p.id}`);
      toast.success("Plan deleted");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-internet-plans-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Internet plans</h1>
          <p className="text-sm text-slate-400 mt-1">
            NBN speed tiers on offer. Partner-scoped plans (e.g. Macktronix) only appear on that partner&apos;s public signup URL. Syncro product codes get the partner&apos;s prefix (e.g. <code className="text-cyan-300 bg-slate-900 px-1 rounded">MACK_NBN100_40</code>) at signup time.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="plan-import-button"
            onClick={() => setImporting(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 px-4 py-2 text-sm font-medium"
          >
            <Download className="w-4 h-4" /> Import from Syncro
          </button>
          <button
            data-testid="plan-create-button"
            onClick={() => setEditing({ ...EMPTY })}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> New plan
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 overflow-hidden">
        {plans === null ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : plans.length === 0 ? (
          <div className="p-10 text-center">
            <Wifi className="w-8 h-8 mx-auto text-slate-600 mb-3" />
            <div className="text-sm text-slate-500">No plans yet. Add your first NBN speed tier.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <SortHeader label="Plan" field="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Speed" field="speed" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Monthly" field="monthly_price" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                <SortHeader label="Partner" field="partner_code" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <SortHeader label="Resolved Syncro code" field="syncro" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {sortedPlans.map(p => (
                <tr key={p.id} className="hover:bg-slate-950/40" data-testid={`plan-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-100 flex items-center gap-2 flex-wrap">
                      {p.name}
                      <FlowBadge flow={p.flow_type} />
                      {!p.active && <span className="text-[10px] uppercase tracking-widest bg-slate-800 text-slate-400 rounded px-1.5 py-0.5">off</span>}
                    </div>
                    {p.description && <div className="text-xs text-slate-500 truncate max-w-xs">{p.description}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">
                    {p.speed_download_mbps || "—"}/{p.speed_upload_mbps || "—"} Mbps
                  </td>
                  <td className="px-4 py-3 text-right font-mono">${(p.monthly_price || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-xs">
                    {p.partner_code ? (
                      <span className="inline-flex items-center rounded-md bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 px-2 py-0.5 font-mono">{p.partner_code}</span>
                    ) : (
                      <span className="text-slate-500">— all —</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">
                    {p.resolved_syncro_code || <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`plan-edit-${p.id}`}
                        onClick={() => setEditing({...p, features: p.features || [], technologies: p.technologies || []})}
                        className="text-slate-400 hover:text-cyan-300 text-xs px-2 py-1 rounded hover:bg-slate-800"
                      >Edit</button>
                      <button
                        data-testid={`plan-delete-${p.id}`}
                        onClick={() => del(p)}
                        className="text-red-400 hover:text-red-300 text-xs px-2 py-1 rounded hover:bg-slate-800"
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <PlanModal plan={editing} partners={partners} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}
      {importing && (
        <SyncroImportModal
          partners={partners}
          onClose={() => setImporting(false)}
          onImported={() => { setImporting(false); reload(); }}
        />
      )}
    </div>
  );
}

function SyncroImportModal({ partners, onClose, onImported }) {
  const [partnerCode, setPartnerCode] = useState(partners[0]?.code || "");
  const [prefix, setPrefix] = useState("");
  const [priceSource, setPriceSource] = useState("retail");
  const [flowType, setFlowType] = useState("");  // "" | "transfer" | "new"
  const [saveAsRule, setSaveAsRule] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (partnerCode && !prefix) {
      setPrefix(`${partnerCode.slice(0, 4).toUpperCase()}_`);
    }
  }, [partnerCode, prefix]);

  async function runPreview() {
    if (!prefix.trim()) { toast.error("Enter a Syncro SKU prefix first"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/commissions/internet-plans/syncro-preview", { params: { prefix: prefix.trim() } });
      setPreview(data);
      if (data.count === 0) {
        toast.warning(`No Syncro products found with SKU starting "${data.prefix}".`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally { setLoading(false); }
  }

  async function runImport() {
    // Partner scope is optional — empty = shared / global plan (visible to
    // every partner on their signup URL). Prefix is always required.
    if (!prefix.trim()) return;
    if (!preview || preview.count === 0) {
      toast.error("Nothing to import — run preview first"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/commissions/internet-plans/import-from-syncro", {
        partner_code: partnerCode || null,
        prefix: prefix.trim(),
        price_source: priceSource,
        flow_type: flowType || null,
        save_as_rule: saveAsRule,
      });
      const scope = partnerCode
        ? `${partners.find(p => p.code === partnerCode)?.name || partnerCode} only`
        : "shared — all partners";
      toast.success(`Imported ${data.created} new, updated ${data.updated} · ${scope}${saveAsRule ? " · auto-sync ON" : ""}`);
      onImported();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl border border-slate-800 bg-slate-900 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="syncro-import-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-xl tracking-tight flex items-center gap-2">
              <Package className="w-5 h-5 text-cyan-400" /> Import Syncro products as plans
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Pulls every synced Syncro Wholesale product whose SKU starts with your prefix and materialises it as a partner-scoped internet plan. Idempotent — re-running refreshes prices instead of duplicating.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Partner scope</span>
            <select value={partnerCode} onChange={(e) => setPartnerCode(e.target.value)} data-testid="import-partner" className={inputCls}>
              <option value="">Standard — all partners (shared)</option>
              {partners.map(p => <option key={p.code} value={p.code}>{p.name} ({p.code}) only</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Syncro SKU prefix</span>
            <input
              value={prefix}
              onChange={(e) => { setPrefix(e.target.value); setPreview(null); }}
              placeholder="MACK_"
              data-testid="import-prefix"
              className={inputCls + " font-mono"}
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Customer flow</span>
            <select value={flowType} onChange={(e) => setFlowType(e.target.value)} data-testid="import-flow" className={inputCls}>
              <option value="">Both — show everywhere</option>
              <option value="transfer">Transferring in (existing customers)</option>
              <option value="new">New customer (fresh signup)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Price source</span>
            <select value={priceSource} onChange={(e) => setPriceSource(e.target.value)} data-testid="import-price-source" className={inputCls}>
              <option value="retail">Syncro retail price</option>
              <option value="cost">Syncro cost price</option>
            </select>
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-300 mb-4 cursor-pointer">
          <input type="checkbox" checked={saveAsRule} onChange={(e) => setSaveAsRule(e.target.checked)} data-testid="import-save-rule" className="w-4 h-4 rounded border-slate-700 accent-cyan-500" />
          <span>Enable <b>auto-sync</b> — any new Syncro product with this prefix will automatically become a plan on the hourly sync tick.</span>
        </label>

        <div className="flex items-center gap-2 mb-4">
          <button
            type="button"
            onClick={runPreview}
            disabled={loading || !prefix.trim()}
            data-testid="import-preview-button"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 hover:bg-slate-800 disabled:opacity-40 text-slate-100 px-4 py-2 text-sm"
          >
            {loading ? "Loading…" : "Preview matches"}
          </button>
          {preview && preview.count > 0 && (
            <div className="text-xs text-slate-400">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 inline mr-1" />
              {preview.count} product{preview.count === 1 ? "" : "s"} match — will import as{" "}
              <b>{partners.find(p => p.code === partnerCode)?.name || partnerCode}</b>
              {flowType && <> for <b>{flowType === "transfer" ? "Transferring in" : "New customers"}</b> only</>}
            </div>
          )}
        </div>

        {preview !== null && (
          <div className="rounded-lg border border-slate-800 overflow-hidden mb-4 max-h-[40vh] overflow-y-auto">
            {preview.count === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400 flex items-center justify-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400" />
                <span>No Syncro products found with SKU starting <code className="text-white font-mono">{preview.prefix}</code>. Add products to Syncro under the Wholesale category first.</span>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-950 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-left px-3 py-2">Plan name</th>
                    <th className="text-right px-3 py-2">Speed</th>
                    <th className="text-right px-3 py-2">Retail</th>
                    <th className="text-right px-3 py-2">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {preview.products.map(p => (
                    <tr key={p.syncro_product_id}>
                      <td className="px-3 py-2 font-mono text-cyan-300">{p.upc_code}</td>
                      <td className="px-3 py-2 text-slate-200">{p.name}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-400">
                        {p.speed_download_mbps || "—"}/{p.speed_upload_mbps || "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">${(p.price_retail ?? 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500">${(p.price_cost ?? 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm">Cancel</button>
          <button
            type="button"
            disabled={busy || !preview || preview.count === 0}
            onClick={runImport}
            data-testid="import-commit-button"
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 px-5 py-2 rounded-lg text-sm font-semibold"
          >
            <Download className="w-4 h-4" /> {busy ? "Importing…" : `Import ${preview?.count || 0} plan${preview?.count === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
function SortHeader({ label, field, sortBy, sortDir, onSort, align = "left" }) {
  const active = sortBy === field;
  const Icon = active ? (sortDir === "asc" ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <th
      onClick={() => onSort(field)}
      data-testid={`plan-sort-${field}`}
      className={`text-${align} px-4 py-3 select-none cursor-pointer hover:text-slate-300 transition-colors`}
    >
      <span className={`inline-flex items-center gap-1 ${active ? "text-cyan-300" : ""}`}>
        {label}
        <Icon className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`} />
      </span>
    </th>
  );
}


function FlowBadge({ flow }) {
  // Small coloured pill so admin can eyeball which customer link each plan
  // belongs to at a glance — handy when managing dozens of imported SKUs.
  if (flow === "transfer") {
    return (
      <span
        data-testid="flow-badge-transfer"
        title="Locked to /signup/{partner}/transfer"
        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] font-semibold rounded-md px-1.5 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/30"
      >
        ↻ Transfer
      </span>
    );
  }
  if (flow === "new") {
    return (
      <span
        data-testid="flow-badge-new"
        title="Locked to /signup/{partner}/new"
        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] font-semibold rounded-md px-1.5 py-0.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
      >
        ✦ New
      </span>
    );
  }
  return null;
}

function PlanModal({ plan, partners, onClose, onSaved }) {
  const [form, setForm] = useState(plan);
  const [featuresText, setFeaturesText] = useState((plan.features || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const isNew = !plan.id;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      features: featuresText.split("\n").map(s => s.trim()).filter(Boolean),
      partner_code: form.partner_code || null,
    };
    try {
      if (isNew) await api.post("/commissions/internet-plans", payload);
      else await api.patch(`/commissions/internet-plans/${plan.id}`, payload);
      toast.success(isNew ? "Plan created" : "Plan saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="plan-modal">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display text-xl tracking-tight">{isNew ? "New plan" : "Edit plan"}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100"><X className="w-5 h-5"/></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Name" required span="full">
            <input data-testid="plan-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={inputCls} placeholder="NBN 100/40" />
          </Field>
          <Field label="Download (Mbps)">
            <input data-testid="plan-down" type="number" value={form.speed_download_mbps || ""} onChange={(e) => setForm({...form, speed_download_mbps: Number(e.target.value) || null})} className={inputCls + " font-mono"} />
          </Field>
          <Field label="Upload (Mbps)">
            <input data-testid="plan-up" type="number" value={form.speed_upload_mbps || ""} onChange={(e) => setForm({...form, speed_upload_mbps: Number(e.target.value) || null})} className={inputCls + " font-mono"} />
          </Field>
          <Field label="Monthly price ($)">
            <input data-testid="plan-monthly" type="number" step="0.01" value={form.monthly_price || 0} onChange={(e) => setForm({...form, monthly_price: Number(e.target.value)})} className={inputCls + " font-mono"} />
          </Field>
          <Field label="Activation fee ($)">
            <input data-testid="plan-activation" type="number" step="0.01" value={form.activation_fee || 0} onChange={(e) => setForm({...form, activation_fee: Number(e.target.value)})} className={inputCls + " font-mono"} />
          </Field>
          <Field label="Contract months (0 = month-to-month)">
            <input data-testid="plan-contract" type="number" value={form.contract_months || 0} onChange={(e) => setForm({...form, contract_months: Number(e.target.value)})} className={inputCls + " font-mono"} />
          </Field>
          <Field label="Sort order">
            <input data-testid="plan-sort" type="number" value={form.sort_order || 0} onChange={(e) => setForm({...form, sort_order: Number(e.target.value)})} className={inputCls + " font-mono"} />
          </Field>
          <Field label="Description" span="full">
            <textarea data-testid="plan-description" rows={2} value={form.description || ""} onChange={(e) => setForm({...form, description: e.target.value})} className={inputCls} />
          </Field>
          <Field label="Features (one per line)" span="full">
            <textarea data-testid="plan-features" rows={3} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} className={inputCls + " font-mono text-xs"} placeholder="Unlimited data&#10;Free static IP&#10;Business hours support" />
          </Field>
          {/* Available-on technologies — partners filter their price
              list by these when they know a specific customer's site
              tech. Empty selection = "available on any tech". */}
          <Field label="Available on technologies" span="full">
            <div className="flex flex-wrap gap-2" data-testid="plan-technologies">
              {NBN_TECHS.map((t) => {
                const on = (form.technologies || []).includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    data-testid={`plan-tech-${t}`}
                    onClick={() => {
                      const current = new Set(form.technologies || []);
                      if (on) current.delete(t); else current.add(t);
                      setForm({ ...form, technologies: Array.from(current) });
                    }}
                    className={
                      "px-3 py-1 rounded-full text-xs font-mono font-semibold border transition-colors " +
                      (on
                        ? "bg-cyan-500/15 border-cyan-500/50 text-cyan-200"
                        : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600")
                    }
                  >
                    {t}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              Leave all off = available on any technology. Tick the ones NBN Co supplies at customer premises for this plan.
            </div>
          </Field>
          <Field label="Partner (leave blank for all)">
            <select data-testid="plan-partner" value={form.partner_code || ""} onChange={(e) => setForm({...form, partner_code: e.target.value})} className={inputCls}>
              <option value="">— all customers —</option>
              {partners.map(pt => <option key={pt.code} value={pt.code}>{pt.name} ({pt.code})</option>)}
            </select>
          </Field>
          <Field label="Customer flow (which signup URL shows this plan)">
            <select
              data-testid="plan-flow-type"
              value={form.flow_type || ""}
              onChange={(e) => setForm({...form, flow_type: e.target.value || null})}
              className={inputCls}
            >
              <option value="">Both — no lock (legacy behaviour)</option>
              <option value="transfer">Transferring in — /signup/{form.partner_code || "{code}"}/transfer</option>
              <option value="new">New Connections — /signup/{form.partner_code || "{code}"}/new</option>
            </select>
          </Field>
          <Field label="Active">
            <label className="flex items-center gap-2 mt-2 text-sm">
              <input data-testid="plan-active" type="checkbox" checked={!!form.active} onChange={(e) => setForm({...form, active: e.target.checked})} />
              Show on the signup wizard
            </label>
          </Field>
          <Field label="Base Syncro product code" span="full">
            <input data-testid="plan-syncro-code" value={form.syncro_product_code || ""} onChange={(e) => setForm({...form, syncro_product_code: e.target.value})} className={inputCls + " font-mono"} placeholder="NBN100_40" />
          </Field>
          <Field label="Syncro product prefix (partner-scoped plans only)" span="full">
            <input data-testid="plan-syncro-prefix" value={form.syncro_product_prefix || ""} onChange={(e) => setForm({...form, syncro_product_prefix: e.target.value})} className={inputCls + " font-mono"} placeholder="MACK_" />
            {(form.syncro_product_code || form.syncro_product_prefix) && (
              <div className="text-xs text-slate-500 mt-1.5">
                Will resolve to <code className="text-cyan-300 bg-slate-950 px-1 rounded" data-testid="plan-resolved-code">{`${form.syncro_product_prefix || ""}${form.syncro_product_code || ""}`}</code>
              </div>
            )}
          </Field>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm">Cancel</button>
            <button data-testid="plan-save" type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60">
              <Save className="w-4 h-4" /> {busy ? "Saving…" : (isNew ? "Create" : "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, required, span }) {
  const cls = span === "full" ? "block md:col-span-2" : "block";
  return (
    <label className={cls}>
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
        {label} {required && <span className="text-red-400">*</span>}
      </span>
      {children}
    </label>
  );
}
